/** @format */

import { createSlice } from "@reduxjs/toolkit";
import { ISandboxCircle, ISandboxShareResult } from "../../utility/types/sandboxTypes";
import {
    createQuickDemoCircle,
    createCustomSandboxCircle,
    fetchSandboxCircle,
    fetchSandboxCircleByToken,
    updateSandboxMemberStatus,
    updateSandboxMemberDayStatus,
    createSandboxShareLink,
    convertSandboxCircle,
} from "../actions/sandboxAction";

interface SandboxState {
    circle: ISandboxCircle | null;
    shareResult: ISandboxShareResult | null;
    isLoading: boolean;
    isMemberUpdating: boolean;
    isSharing: boolean;
    isConverting: boolean;
    error: string | null;
    memberUpdateError: string | null;
    shareError: string | null;
    convertError: string | null;
}

const initialState: SandboxState = {
    circle: null,
    shareResult: null,
    isLoading: false,
    isMemberUpdating: false,
    isSharing: false,
    isConverting: false,
    error: null,
    memberUpdateError: null,
    shareError: null,
    convertError: null,
};

const sandboxSlice = createSlice({
    name: "sandbox",
    initialState,
    reducers: {
        clearSandboxState: () => initialState,
        clearSandboxError: (state) => {
            state.error = null;
            state.memberUpdateError = null;
            state.shareError = null;
            state.convertError = null;
        },
    },
    extraReducers: (builder) => {
        // ── createQuickDemoCircle ────────────────────────────────────────────
        builder
            .addCase(createQuickDemoCircle.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createQuickDemoCircle.fulfilled, (state, action) => {
                state.isLoading = false;
                state.circle = action.payload;
            })
            .addCase(createQuickDemoCircle.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // ── createCustomSandboxCircle ────────────────────────────────────────
        builder
            .addCase(createCustomSandboxCircle.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createCustomSandboxCircle.fulfilled, (state, action) => {
                state.isLoading = false;
                state.circle = action.payload;
            })
            .addCase(createCustomSandboxCircle.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // ── fetchSandboxCircle ───────────────────────────────────────────────
        builder
            .addCase(fetchSandboxCircle.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSandboxCircle.fulfilled, (state, action) => {
                state.isLoading = false;
                state.circle = action.payload;
            })
            .addCase(fetchSandboxCircle.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // ── fetchSandboxCircleByToken ────────────────────────────────────────
        builder
            .addCase(fetchSandboxCircleByToken.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSandboxCircleByToken.fulfilled, (state, action) => {
                state.isLoading = false;
                state.circle = action.payload;
            })
            .addCase(fetchSandboxCircleByToken.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // ── updateSandboxMemberStatus ────────────────────────────────────────
        builder
            .addCase(updateSandboxMemberStatus.pending, (state) => {
                state.isMemberUpdating = true;
                state.memberUpdateError = null;
            })
            .addCase(updateSandboxMemberStatus.fulfilled, (state, action) => {
                state.isMemberUpdating = false;
                state.circle = action.payload;
            })
            .addCase(updateSandboxMemberStatus.rejected, (state, action) => {
                state.isMemberUpdating = false;
                state.memberUpdateError = action.payload as string;
            });
        // ── updateSandboxMemberDayStatus ─────────────────────────────
        builder
            .addCase(updateSandboxMemberDayStatus.pending, (state) => {
                state.isMemberUpdating = true;
                state.memberUpdateError = null;
            })
            .addCase(updateSandboxMemberDayStatus.fulfilled, (state, action) => {
                state.isMemberUpdating = false;
                state.circle = action.payload;
            })
            .addCase(updateSandboxMemberDayStatus.rejected, (state, action) => {
                state.isMemberUpdating = false;
                state.memberUpdateError = action.payload as string;
            });
        // ── createSandboxShareLink ───────────────────────────────────────────
        builder
            .addCase(createSandboxShareLink.pending, (state) => {
                state.isSharing = true;
                state.shareError = null;
            })
            .addCase(createSandboxShareLink.fulfilled, (state, action) => {
                state.isSharing = false;
                state.shareResult = action.payload;
                if (state.circle) {
                    state.circle.shareToken = action.payload.shareToken;
                }
            })
            .addCase(createSandboxShareLink.rejected, (state, action) => {
                state.isSharing = false;
                state.shareError = action.payload as string;
            });

        // ── convertSandboxCircle ─────────────────────────────────────────────
        builder
            .addCase(convertSandboxCircle.pending, (state) => {
                state.isConverting = true;
                state.convertError = null;
            })
            .addCase(convertSandboxCircle.fulfilled, (state) => {
                state.isConverting = false;
                if (state.circle) {
                    state.circle.sandboxStatus = "converted";
                }
            })
            .addCase(convertSandboxCircle.rejected, (state, action) => {
                state.isConverting = false;
                state.convertError = action.payload as string;
            });
    },
});

export const { clearSandboxState, clearSandboxError } = sandboxSlice.actions;
export default sandboxSlice.reducer;
